// src/features/inventory/pages/ItemDetailPage/tabs/Value/PriceChart.jsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { formatDKK } from '../../../../../../utils/formatters/currencyFormatter';
import { parseApiTimestamp } from '../../../../../../utils/formatters/dateFormatter';

/**
 * Round axis ticks around the prices. Prices move a few percent, so a zero
 * baseline would flatten every change: the axis spans the range plus a
 * margin instead, on whole steps (1, 2 or 5 × a power of ten).
 */
const priceTicks = (min, max) => {
  const span = Math.max(max - min, max * 0.1, 0.01);
  const raw = span / 3;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= raw);
  const low = Math.max(0, Math.floor((min - span * 0.25) / step) * step);
  const high = Math.ceil((max + span * 0.25) / step) * step;
  const ticks = [];
  for (let tick = low; tick <= high + step / 2; tick += step) ticks.push(Number(tick.toFixed(6)));
  return ticks;
};

/**
 * The cost price over time as a step line: a price holds until the next
 * change, so the line only moves on the day it changed. The last price is
 * carried to today. One series, so the section title names it (no legend).
 */
export default function PriceChart({ history, uom }) {
  const { t } = useTranslation(['inventory']);
  const theme = useTheme();

  const points = useMemo(() => {
    // Entries without a price (an undo back to no price) are left out; the
    // table below still lists them
    const changes = history
      .filter((entry) => entry.cost_price != null)
      .map((entry) => ({ at: parseApiTimestamp(entry.created_at)?.getTime(), price: Number(entry.cost_price) }))
      .filter((p) => p.at != null)
      .sort((a, b) => a.at - b.at);
    if (changes.length === 0) return [];
    const now = Date.now();
    return [...changes, { at: Math.max(now, changes[changes.length - 1].at), price: changes[changes.length - 1].price, today: true }];
  }, [history]);

  if (points.length === 0) return null;

  const prices = points.map((p) => p.price);

  const ticks = priceTicks(Math.min(...prices), Math.max(...prices));
  const wholeKroner = ticks.every((tick) => Number.isInteger(tick));
  // Changes within a day or two (a correction, an undo) need times on the axis,
  // or every tick reads the same date
  const span = points[points.length - 1].at - points[0].at;
  const shortSpan = span < 2 * 24 * 60 * 60 * 1000;
  let axisFormat = 'd. MMM yy';
  if (span < 60 * 60 * 1000) axisFormat = 'HH:mm:ss';
  else if (shortSpan) axisFormat = 'HH:mm';

  return (
    <Box sx={{ height: 200, mb: 2 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
          <CartesianGrid stroke={theme.palette.divider} vertical={false} />
          <XAxis
            dataKey="at"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(value) => format(value, axisFormat, { locale: da })}
            tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
            stroke={theme.palette.divider}
            minTickGap={32}
          />
          <YAxis
            domain={[ticks[0], ticks[ticks.length - 1]]}
            ticks={ticks}
            tickFormatter={(value) => formatDKK(value, { whole: wholeKroner })}
            tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
            stroke={theme.palette.divider}
            width={88}
          />
          <Tooltip
            formatter={(value) => [`${formatDKK(value)} / ${uom}`, t('inventory:items.tabs.value.history.price')]}
            labelFormatter={(value, payload) => (payload?.[0]?.payload?.today
              ? t('inventory:items.tabs.value.history.today')
              : format(value, shortSpan ? "d. MMMM yyyy 'kl.' HH:mm" : 'd. MMMM yyyy', { locale: da }))}
            contentStyle={{
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 8,
            }}
            itemStyle={{ color: theme.palette.text.primary }}
          />
          <Line
            type="stepAfter"
            dataKey="price"
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            dot={(props) => (props.payload.today ? <g key={props.key} /> : (
              <circle
                key={props.key}
                cx={props.cx}
                cy={props.cy}
                r={4}
                fill={theme.palette.primary.main}
                stroke={theme.palette.background.paper}
                strokeWidth={2}
              />
            ))}
            activeDot={{ r: 5, strokeWidth: 2, stroke: theme.palette.background.paper }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}
